import { useState, useCallback, useMemo } from 'react'
import { heapEvent } from '@equipmentshare/heaper'
import { useUpdateDemandRequestLineItemFulfillerNotesMutation } from '../../../graphql'

type LineItem = {
	lineItemId: string
	fulfillerNotes?: string | null
}

type DemandRequest = {
	id: string
	lineItems: LineItem[]
}

type UseFulfillerNotesParams = {
	request: DemandRequest | undefined | null
	addToast: ( message: string ) => void
}

/**
 * Hook for managing fulfiller notes modal and updates
 */
export function useFulfillerNotes( { request, addToast }: UseFulfillerNotesParams ) {
	const [ fulfillerNotesModalLineItemId, setFulfillerNotesModalLineItemId ] = useState<string | null>( null )
	const [ updateFulfillerNotes ] = useUpdateDemandRequestLineItemFulfillerNotesMutation()

	const handleEditFulfillerNote = useCallback( ( lineItemId: string ) => {
		setFulfillerNotesModalLineItemId( lineItemId )
		heapEvent( 'Inventory - Stock Request - Line Item - Click - Add/Edit Fulfillment Note', {
			demand_request_id: request?.id,
			line_item_id: lineItemId,
		} )
	}, [ request?.id ] )

	const handleSaveFulfillerNote = useCallback( async( notes: string ) => {
		if ( !request || !fulfillerNotesModalLineItemId ) {
			return
		}

		try {
			const result = await updateFulfillerNotes( {
				variables: {
					input: {
						demandRequestId: request.id,
						lineItemId: fulfillerNotesModalLineItemId,
						fulfillerNotes: notes.trim() || null,
					},
				},
			} )

			if ( result.data?.updateDemandRequestLineItemFulfillerNotes?.success ) {
				addToast( 'Fulfillment note updated' )
				heapEvent( 'Inventory - Stock Request - Line Item - Save - Fulfillment Note', {
					demand_request_id: request.id,
					line_item_id: fulfillerNotesModalLineItemId,
					has_note: !!notes.trim(),
				} )
			} else {
				const errorMessage = result.data?.updateDemandRequestLineItemFulfillerNotes?.message ?? 'Unknown error occurred'
				addToast( `Failed to update fulfillment note: ${errorMessage}` )
			}
		} catch ( err ) {
			addToast( `Failed to update fulfillment note: ${err instanceof Error ? err.message : 'Unknown error'}` )
		}
	}, [ request, fulfillerNotesModalLineItemId, updateFulfillerNotes, addToast ] )

	const handleCloseNotesModal = useCallback( () => {
		setFulfillerNotesModalLineItemId( null )
	}, [] )

	// Get the current line item's fulfiller notes for the modal
	const currentLineItem = useMemo( () =>
		fulfillerNotesModalLineItemId
			? request?.lineItems.find( ( item ) => item.lineItemId === fulfillerNotesModalLineItemId )
			: null,
	[ fulfillerNotesModalLineItemId, request?.lineItems ] )

	return {
		fulfillerNotesModalLineItemId,
		currentLineItem,
		handleEditFulfillerNote,
		handleSaveFulfillerNote,
		handleCloseNotesModal,
	}
}
